import React from 'react';
import TextField from '@material-ui/core/TextField';
import Autocomplete from '@material-ui/lab/Autocomplete';
import useCenterlineAiStyles from './styles';
import { LiveProject, useCenterlineAi } from './CenterlineAiProvider';

/**
 * The project every question is asked about (chat-ui-spec §2.5). The list is CENTERLINE's live
 * projects the user may ask about; it filters by name or by a pasted id, and only a listed project
 * can be chosen. Choosing one is always a real project change: new chat, "@" cleared, chats
 * reloaded.
 */

const HELPER = 'Every query runs read-only inside this project';

const filterProjects = (options: LiveProject[], { inputValue }: { inputValue: string }) => {
  const q = inputValue.trim().toLowerCase();
  if (!q) return options;
  return options.filter((p) => p.name.toLowerCase().includes(q) || p.id.toLowerCase().includes(q));
};

export function ProjectSelector() {
  const classes = useCenterlineAiStyles();
  const { state, projects, actions } = useCenterlineAi();
  const selected = projects.list.find((p) => p.id === state.projectId) ?? null;

  if (projects.status === 'failed') {
    return (
      <div className={classes.projectFailed} role="alert">
        Failed to load projects
      </div>
    );
  }
  return (
    <Autocomplete
      className={classes.projectSelect}
      classes={{ option: classes.projectOption, paper: classes.projectOption }}
      options={projects.list}
      // Null until a project is chosen; MUI shows an empty box for it.
      value={selected as LiveProject}
      loading={projects.status === 'loading'}
      loadingText="Loading…"
      noOptionsText="No project matches"
      disableClearable
      getOptionLabel={(p) => p.name || p.id}
      getOptionSelected={(a, b) => a.id === b.id}
      filterOptions={filterProjects}
      renderOption={(p) => <span title={p.id}>{p.name || p.id}</span>}
      onChange={(_, value) => {
        if (value && value.id !== state.projectId) actions.selectProject(value.id);
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          variant="outlined"
          label="Project"
          helperText={HELPER}
          placeholder={
            projects.status === 'loading' ? 'Loading…' : 'Filter by name or paste an id…'
          }
          inputProps={{
            ...params.inputProps,
            'aria-label': 'Project — every query is scoped to this project',
          }}
        />
      )}
    />
  );
}
